import {gql} from '@apollo/client'
import {useMemo} from 'react'
import {useListActionFlowConfigsQuery} from './generated/use-action-flow-config'
import {decodeAndMigrate} from '../common/action-flows/migrate'
import {ActionFlowConfig} from '../common/types/action-flows'

export type FlowConfig = {
  id: string
  updated?: string | null | undefined
  name?: string | null | undefined
  currentVersion: {
    config: ActionFlowConfig
  }
  versions: {
    nodes: {
      version: number
      status: string
    }[]
  }
}

export const useActionFlowConfig = ({
  environmentId
}: {
  environmentId: string
}) => {
  const {data, loading} = useListActionFlowConfigsQuery({
    variables: {environmentId}
  })
  const validated = useMemo(() => {
    return data?.actionFlowConfigs?.nodes
      .map((node): FlowConfig | undefined => {
        const validatedConfig = decodeAndMigrate(node.currentVersion?.config)
        if (!validatedConfig.valid) return undefined
        return {
          ...node,
          currentVersion: {
            config: validatedConfig.config
          }
        }
      })
      .filter((n): n is FlowConfig => !!n)
  }, [data?.actionFlowConfigs?.nodes])

  return {data: validated, loading: loading || validated === undefined}
}

gql`
  query ListActionFlowConfigs($environmentId: String!) {
    actionFlowConfigs(
      condition: {environmentId: $environmentId}
      filter: {configStatus: {in: [DRAFT, PUBLISHED]}}
    ) {
      nodes {
        id
        name
        updated
        currentVersion {
          config
        }
        versions: actionFlowConfigVersionsByEnvironmentIdAndFlowConfigId(
          first: 50 # sanity limit
          orderBy: VERSION_DESC
        ) {
          nodes {
            version
            status
          }
        }
      }
    }
  }
`
